'use client';

import { Fragment } from 'react';
import { motion } from 'framer-motion';
import { stegaClean } from '@sanity/client/stega';
import type { ITopBanner } from '@/sanity/lib/definitions';
import { useBannerCountdown } from '@/hooks/useBannerCountdown';
import { getCountdownLabels } from '@/lib/bannerCountdown';
import { RibbonLayer, type RibbonPlacement } from '@/components/global/seasonal/SeasonalLayers';
import { SeasonalBannerCountdown } from '@/components/global/seasonal/SeasonalCountdown';
import {
  BannerPill,
  useBannerCopy,
  type TextSegment,
} from '@/components/global/seasonal/SeasonalBannerParts';

/**
 * Top banner — "Black Month 2026" design. Rendered by
 * components/global/TopBanner.tsx when the document's Design field is
 * "blackMonth".
 *
 * Built from Figma 12770:24986 "Banner" of the BLACK FRIDAY section
 * (1920x64) and 13047:25268 "Banner · mobile" (430x113): the silver light
 * ("luz prata") screened over the black plate, the "BLACK MONTH" satin
 * ribbons as the animated canvas of lib/ribbonType.js at the frames'
 * placements, and the label, the copy, the countdown and the pill as real
 * HTML at the Figma positions. The desktop frame places its three groups
 * absolutely (label at x 304, copy + countdown at the viewport centre, pill
 * ending at x 1617), so the desktop tree does the same in rem; the phone
 * frame is a 12px-padded column: label, copy line, countdown + pill.
 *
 * Copy: `preText` is the label; `text` the copy with its [[...]] part in
 * Blacker Italic and the rest in Geist; `mobileText` replaces `text` on
 * phones when the editor filled it in.
 *
 * Sizes: Figma px / 8 (1rem = 8px at the design width, app/globals.scss;
 * on phones at 435px).
 */

interface TopBannerBlackMonthProps {
  data: ITopBanner;
  language?: string;
}

/**
 * Figma 12770:42567 / 12770:42566: two 900x53 ribbons at 90%, one centred
 * at (-113.8, 64.1) turned -11.56deg, one at (2014.3, 41.9) turned 8deg —
 * the right one anchored to the right edge (1920 - 2014.3 = -94.3) so it
 * keeps its corner at any width.
 */
const DESKTOP_RIBBONS: RibbonPlacement[] = [
  { cx: -113.8, cy: 64.1, width: 900, height: 53, angle: -11.56, opacity: 0.9 },
  { cx: -94.3, cy: 41.9, fromRight: true, width: 900, height: 53, angle: 8, opacity: 0.9 },
];

/**
 * Figma 13047:25308: the phone frame's single ribbon, 601.994x35.451 at 90%
 * turned 8deg, centred at (491.5, 29.4) — anchored to the right edge
 * (430 - 491.5 = -61.5).
 */
const MOBILE_RIBBON: RibbonPlacement = {
  cx: 430 - 491.5,
  cy: 29.44,
  fromRight: true,
  width: 601.994,
  height: 35.451,
  angle: 8,
  opacity: 0.9,
};

const DEFAULT_PRE_TEXT = 'Black Friday Special';

/**
 * The copy's segments as one inline run, spaces between them, so the
 * Blacker and Geist parts sit on one baseline like the Figma text.
 */
function CopySegments({
  segments,
  highlightedClass,
  plainClass,
}: {
  segments: TextSegment[];
  highlightedClass: string;
  plainClass: string;
}) {
  return (
    <>
      {segments.map((segment, index) => (
        <Fragment key={`${index}-${segment.text}`}>
          {index > 0 && ' '}
          <span className={segment.highlighted ? highlightedClass : plainClass}>{segment.text}</span>
        </Fragment>
      ))}
    </>
  );
}

export default function TopBannerBlackMonth({ data, language }: TopBannerBlackMonthProps) {
  const { preText, segments, mobileSegments, ctaLabel, finalLink } = useBannerCopy(data, language);
  const label = preText || DEFAULT_PRE_TEXT;
  const ctaFallbackLabel = stegaClean(preText) || DEFAULT_PRE_TEXT;

  // null while the countdown window is closed -> no countdown at all.
  const timeLeft = useBannerCountdown(data);
  const countdownLabels = getCountdownLabels(stegaClean(language) || undefined);

  return (
    <motion.div
      initial={{ opacity: 0, y: 'calc(-100% - 1.5rem)' }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      className="relative w-full overflow-clip bg-[#0a0b0b] lg:h-[8rem]"
      data-seasonal-banner="blackMonth"
    >
      {/* decorative layers — the silver light and the ribbons */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        {/* 12770:42565 / 13047:25270 "luz prata" — the top 6% of the Figma
            image stretched into the bar (the same crop in both frames),
            screened at 55% on desktop and 30% on phones. */}
        {/* eslint-disable-next-line @next/next/no-img-element -- decorative plate */}
        <img
          src="/seasonal/black-month/banner/light.webp"
          alt=""
          className="absolute left-0 top-0 h-full w-full max-w-none object-fill opacity-30 mix-blend-screen lg:opacity-[0.55]"
        />
        {DESKTOP_RIBBONS.map((placement, index) => (
          <RibbonLayer
            key={index}
            placement={placement}
            index={index}
            frame={{ width: 1920, height: 64 }}
            className="hidden lg:block"
          />
        ))}
        <RibbonLayer placement={MOBILE_RIBBON} frame={{ width: 430, height: 113 }} className="lg:hidden" />
      </div>
      {/* end decorative layers */}

      {/* Phone frame 13047:25271 "Conteúdo": 12px padding, 10px gaps —
          label + line, then countdown and pill at the row's two ends. */}
      <div className="relative z-[1] flex flex-col gap-[1.25rem] p-[1.5rem] lg:hidden">
        <div className="flex flex-col items-start">
          {/* 13047:25305 — Blacker Display Italic 20/24, -0.6 */}
          <p className="m-0 font-blacker text-[2.5rem] font-normal italic leading-[3rem] tracking-[-0.075rem] text-white">
            {label}
          </p>
          {/* 13047:25280 — Blacker Display Italic 14/20 + Geist Regular 14/20, -0.28, white 80% */}
          {mobileSegments.length > 0 && (
            <p className="m-0 font-geist text-[1.75rem] font-normal leading-[2.5rem] tracking-[-0.035rem] text-white/80">
              {/* leading-[0] on the serif part: a Blacker span would stretch the 20px line box by 2px */}
              <CopySegments
                segments={mobileSegments}
                highlightedClass="font-blacker italic leading-[0]"
                plainClass="font-geist"
              />
            </p>
          )}
        </div>
        <div className="flex items-center justify-between gap-[1.5rem]">
          {timeLeft && (
            <SeasonalBannerCountdown
              timeLeft={timeLeft}
              labels={countdownLabels}
              scale={0.889}
              boxHeight={35.336}
            />
          )}
          {finalLink && (
            <BannerPill
              href={finalLink}
              label={ctaLabel}
              fallbackLabel={ctaFallbackLabel}
              className="ml-auto px-[2rem]"
            />
          )}
        </div>
      </div>

      {/* Desktop frame 12770:24986: three groups at their Figma positions. */}
      <div className="absolute inset-0 z-[1] hidden lg:block">
        {/* 12770:24987 — "BLACK FRIDAY SPECIAL" at x 304, Blacker Display
            Italic 22 / -0.66, cap-trimmed and centred on the bar's middle. */}
        <div className="absolute left-[38rem] top-1/2 flex -translate-y-1/2 items-center whitespace-nowrap">
          <span className="font-blacker text-[2.75rem] font-normal italic leading-none tracking-[-0.0825rem] text-white [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
            {label}
          </span>
        </div>

        {/* 12940:7019 — copy + countdown, gap 23, centred on the viewport,
            top 14 (36 tall with the countdown). The copy is
            "[[Nur für kurze Zeit:]] Sichere dir unser exklusives Angebot":
            the [[...]] part Blacker Display Italic 24, the rest Geist
            Regular 22, both -0.44 on a 0.98 line. */}
        <div className="absolute left-1/2 top-[1.75rem] flex -translate-x-1/2 items-center gap-[2.875rem] whitespace-nowrap">
          {segments.length > 0 && (
            <p className="m-0 font-geist text-[2.75rem] font-normal leading-[0.98] tracking-[-0.055rem] text-white">
              <CopySegments
                segments={segments}
                highlightedClass="font-blacker text-[3rem] italic leading-[0.98]"
                plainClass="font-geist"
              />
            </p>
          )}
          {timeLeft && <SeasonalBannerCountdown timeLeft={timeLeft} labels={countdownLabels} />}
        </div>

        {/* 12770:24996 "Link" — x 1453..1617 at 1920, centred on the bar's
            middle (+0.5px); anchored to its right edge so a longer label
            grows leftwards. */}
        {finalLink && (
          <div className="absolute right-[37.875rem] top-[1.75rem]">
            <BannerPill href={finalLink} label={ctaLabel} fallbackLabel={ctaFallbackLabel} className="px-[2rem]" />
          </div>
        )}
      </div>
    </motion.div>
  );
}
